import React from 'react'
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline'

interface StatsCardProps {
  title: string
  value: string
  change: number
  changeType: 'increase' | 'decrease'
  icon: React.ReactNode
  color: 'blue' | 'green' | 'purple' | 'red' | 'yellow'
}

const colorClasses = {
  blue: {
    bg: 'from-blue-500 to-blue-600',
    text: 'text-blue-600',
    light: 'bg-blue-50 border-blue-200'
  },
  green: {
    bg: 'from-green-500 to-green-600',
    text: 'text-green-600',
    light: 'bg-green-50 border-green-200'
  },
  purple: {
    bg: 'from-purple-500 to-purple-600',
    text: 'text-purple-600',
    light: 'bg-purple-50 border-purple-200'
  },
  red: {
    bg: 'from-red-500 to-red-600',
    text: 'text-red-600',
    light: 'bg-red-50 border-red-200'
  },
  yellow: {
    bg: 'from-yellow-500 to-yellow-600',
    text: 'text-yellow-600',
    light: 'bg-yellow-50 border-yellow-200'
  }
}

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  change,
  changeType,
  icon,
  color
}) => {
  const colors = colorClasses[color]

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm card-hover fade-in relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
        <div className={`w-full h-full rounded-full bg-gradient-to-r ${colors.bg} transform translate-x-8 -translate-y-8`}></div>
      </div>

      <div className="relative flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900 leading-tight">{value}</p>

          <div className="flex items-center mt-3">
            <div className={`flex items-center px-2 py-1 rounded-full ${
              changeType === 'increase' ? 'bg-green-50' : 'bg-red-50'
            }`}>
              {changeType === 'increase' ? (
                <ArrowUpIcon className="w-3 h-3 text-green-500 mr-1" />
              ) : (
                <ArrowDownIcon className="w-3 h-3 text-red-500 mr-1" />
              )}
              <span className={`text-xs font-semibold ${
                changeType === 'increase' ? 'text-green-600' : 'text-red-600'
              }`}>
                {Math.abs(change)}%
              </span>
            </div>
            <span className="text-xs text-gray-500 ml-2">vs mês anterior</span>
          </div>
        </div>

        <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${colors.bg} flex items-center justify-center text-white shadow-lg transform transition-transform duration-200 hover:scale-110`}>
          <div className="w-6 h-6">
            {icon}
          </div>
        </div>
      </div>

      {/* Subtle animation bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-100 overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${colors.bg} transform origin-left transition-transform duration-1000 ease-out`}
          style={{
            transform: `scaleX(${Math.min(Math.abs(change) / 100, 1)})`,
            animationDelay: '0.5s'
          }}
        ></div>
      </div>
    </div>
  )
}